import { Icon } from '@components/abb-icon/icons';
import { DashboardSidePanelFormComponent } from '@pages/dashboards/dashboard-side-panel-form/dashboard-side-panel-form.component';
import { OutputSignalContent } from '@utils/angular-ext';
import { MaybePromise } from '@utils/promise';
import { GetDashboardsQuery } from 'gql/graphql';

export enum WidgetType {
  // Energy monitoring
  ELECTRICAL_VALUES = 'electrical-values',
  ENERGY_VALUES = 'energy-values',
  POWER_QUALITY = 'power-quality',
  CUSTOM_VALUES = 'custom-values',
  MAX_MIN_VALUES = 'max-min-values',
  ENERGY_CONSUMPTION = 'energy-consumption',

  // Cost management
  COST_BENCHMARK = 'cost-benchmark',
  ENERGY_COST_BREAKDOWN = 'energy-cost-breakdown',
  CONTRACTS_AND_TARIFFS = 'contracts-and-tariffs',

  // Non electrical parameters
  WATER_GAS = 'water-gas',

  // Alarms monitoring
  N_OF_ALARMS = 'n-of-alarms',
  ALARM_LOG = 'alarm-log',

  // Control
  AUTOMATIONS_LOG = 'automations-log',

  // Asset monitoring
  DEVICE_STATUS = 'device-status',
  ONLINE_EVENTS = 'online-events',

  // Sustainability
  CO2_EMISSIONS = 'co2-emissions',

  // Properties
  TEXT = 'text',
  LOCATION = 'location',

  // Data analysis
  ACTUAL_VS_TARGET_VALUES = 'actual-vs-target-values',
  KPI_CALCULATION = 'kpi-calculation',
}

export enum WidgetTypeCategory {
  ENERGY_MONITORING = 'energy-monitoring',
  NON_ELECTRICAL_PARAMETERS = 'non-electrical-parameters',
  COST_MANAGEMENT = 'cost-management',
  ALARMS_MONITORING = 'alarms-monitoring',
  CONTROL = 'control',
  DATA_ANALYSIS = 'data-analysis',
  ASSET_MONITORING = 'asset-monitoring',
  SUSTAINABILITY = 'sustainability',
  PROPERTIES = 'properties',
}

export type DashboardSidePanelData = {
  dashboardId: string;
  widgets: GetDashboardsQuery['getDashboards'][number]['widgets'];
  widget?: GetDashboardsQuery['getDashboards'][number]['widgets'][number];
  mode: 'edit' | 'clone' | 'new';
  // TODO: refactor splitting dashboard side panel in widget side panel and dashboard specialization with apollo endpoints
  submitOverride?: (
    params: OutputSignalContent<DashboardSidePanelFormComponent['abbSubmit']>
  ) => MaybePromise<void>;
};

export type WidgetTypeDef = {
  icon: Icon;
  name: string;
  id: WidgetType;
  description: string;
};

export type WidgetTypeCategoryDef = {
  name: string;
  id: string;
  widgetTypes: WidgetTypeDef[];
};

export const COLS = 100;
export const EXTRA_ROWS = 100;

export enum WidgetDisplayOption {
  CHART_LINEAR = 'chart-linear',
  TABLE = 'table',
  GAUGE = 'gauge',
  CHART_DONUT = 'chart-donut',
  CHART_COLUMN = 'chart-column',
  MIN_MAX = 'min-max',
  WATER_GAS = 'water-gas',
  CONTRACTS_TARIFFS = 'contracts-tariffs',
  NUMBER_AND_LIST = 'number-and-list',
  LIST_WITH_TARGET = 'list-with-target',
  RICH_TEXT = 'rich-text',
  MAP = 'map',
  KPI = 'kpi',
}

export enum WidgetCriteria {
  DEVICE = 'device',
  GROUP = 'group',
  PROPERTY = 'property',
}

export enum WidgetVisualization {
  SINGLE = 'single',
  AVERAGE = 'average',
}

export enum WidgetTimeframe {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_WEEK = 'last-week',
  LAST_MONTH = 'last-month',
  LAST_YEAR = 'last-year',
  SPECIFIC_DATE = 'specific-date',
}

// TODO: le dimensioni di default probabilmente andrebbero definite anche per il WidgetType (es. le tabelle ha senso che abbiano altezze diverse)

export const WidgetDisplayOptionMapping = {
  [WidgetDisplayOption.CHART_LINEAR]: {
    name: 'Chart (linear)',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.CHART_LINEAR,
  },
  [WidgetDisplayOption.TABLE]: {
    name: 'Table',
    rowSpan: 20,
    colSpan: 45,
    id: WidgetDisplayOption.TABLE,
  },
  [WidgetDisplayOption.GAUGE]: {
    name: 'Gauge',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.GAUGE,
  },
  [WidgetDisplayOption.CHART_COLUMN]: {
    name: 'Chart (column)',
    rowSpan: 30,
    colSpan: 45,
    id: WidgetDisplayOption.CHART_COLUMN,
  },
  [WidgetDisplayOption.CHART_DONUT]: {
    name: 'Chart (donut)',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.CHART_DONUT,
  },
  [WidgetDisplayOption.MIN_MAX]: {
    name: 'Max / Min',
    rowSpan: 25,
    colSpan: 40,
    id: WidgetDisplayOption.MIN_MAX,
  },
  [WidgetDisplayOption.WATER_GAS]: {
    name: 'Water / Gas',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.WATER_GAS,
  },
  [WidgetDisplayOption.CONTRACTS_TARIFFS]: {
    name: 'Contracts & Tariffs',
    rowSpan: 40,
    colSpan: 45,
    id: WidgetDisplayOption.CONTRACTS_TARIFFS,
  },
  [WidgetDisplayOption.NUMBER_AND_LIST]: {
    name: 'Number and list',
    rowSpan: 20,
    colSpan: 30,
    id: WidgetDisplayOption.NUMBER_AND_LIST,
  },
  [WidgetDisplayOption.LIST_WITH_TARGET]: {
    name: 'List with target',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.LIST_WITH_TARGET,
  },
  [WidgetDisplayOption.RICH_TEXT]: {
    name: 'Text',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.RICH_TEXT,
  },
  [WidgetDisplayOption.MAP]: {
    name: 'Map',
    rowSpan: 30,
    colSpan: 30,
    id: WidgetDisplayOption.MAP,
  },
  [WidgetDisplayOption.KPI]: {
    name: 'KPI',
    rowSpan: 20,
    colSpan: 15,
    id: WidgetDisplayOption.KPI,
  },
};

export const WidgetDisplayOptionsByType = {
  [WidgetType.ELECTRICAL_VALUES]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_LINEAR],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
    WidgetDisplayOptionMapping[WidgetDisplayOption.GAUGE],
  ],
  [WidgetType.ENERGY_VALUES]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_COLUMN],
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_DONUT],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
    WidgetDisplayOptionMapping[WidgetDisplayOption.GAUGE],
  ],
  [WidgetType.POWER_QUALITY]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_LINEAR],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.CUSTOM_VALUES]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_LINEAR],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.COST_BENCHMARK]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_COLUMN],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.ENERGY_COST_BREAKDOWN]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_COLUMN],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.MAX_MIN_VALUES]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.MIN_MAX],
  ],
  [WidgetType.ENERGY_CONSUMPTION]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_DONUT],
    WidgetDisplayOptionMapping[WidgetDisplayOption.CHART_COLUMN],
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.WATER_GAS]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.WATER_GAS],
  ],
  [WidgetType.CONTRACTS_AND_TARIFFS]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.CONTRACTS_TARIFFS],
  ],
  [WidgetType.N_OF_ALARMS]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.NUMBER_AND_LIST],
  ],
  [WidgetType.ALARM_LOG]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.AUTOMATIONS_LOG]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.DEVICE_STATUS]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.ONLINE_EVENTS]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.CO2_EMISSIONS]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.LIST_WITH_TARGET],
  ],
  [WidgetType.TEXT]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.RICH_TEXT],
  ],
  [WidgetType.LOCATION]: [WidgetDisplayOptionMapping[WidgetDisplayOption.MAP]],
  [WidgetType.ACTUAL_VS_TARGET_VALUES]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.TABLE],
  ],
  [WidgetType.KPI_CALCULATION]: [
    WidgetDisplayOptionMapping[WidgetDisplayOption.KPI],
  ],
};

export enum YAxis {
  AUTOSCALE = 'autoscale',
  MANUAL = 'manual',
}
